import { Box, Chip, Grid, Stack } from '@mui/material';
import { defineMessages, IntlShape, useIntl } from 'react-intl';
import { ReactNode, useRef, useState } from 'react';
import PersonOutlinedIcon from '@mui/icons-material/PersonOutlined';
import EmailOutlinedIcon from '@mui/icons-material/EmailOutlined';
import VerifiedUserOutlinedIcon from '@mui/icons-material/VerifiedUserOutlined';
import AccountBalanceOutlinedIcon from '@mui/icons-material/AccountBalanceOutlined';
import RateReviewOutlinedIcon from '@mui/icons-material/RateReviewOutlined';
import LoyaltyOutlinedIcon from '@mui/icons-material/LoyaltyOutlined';
import WorkspacePremiumOutlinedIcon from '@mui/icons-material/WorkspacePremiumOutlined';
import CalendarMonthOutlinedIcon from '@mui/icons-material/CalendarMonthOutlined';
import ContentCopyOutlinedIcon from '@mui/icons-material/ContentCopyOutlined';
import AlternateEmailOutlinedIcon from '@mui/icons-material/AlternateEmailOutlined';
import DetailColumn from '@shared/components/DetailColumn';
import { ImperativeFormDialogRefType, UserProfilDialog } from '@shared/components/UserFormDialog';
import { isSubscriptionExpert } from '@shared/entity';
import AppTheme from '@libs/mui/AppTheme';
import { UserData } from '@pages/profile';
import { useSubscriptionPlan } from '@shared/store';
import { useSubscriptionQuery, useUpdateUserMutation } from '@shared/queries';
import { calculateRemainingDays } from '@features/subscription/entity';
import { generateBankAlias } from '@features/profile/entity';
import PublicProfileSection from './PublicProfileSection';

type ProfileInfoSectionProps = {
  userData: UserData;
  userEmail?: string;
  userSub?: string;
  isExpertPlan: boolean;
};

type ProfileFieldConfig = {
  descriptor: { id: string; defaultMessage: string };
  getValue: (props: ProfileInfoSectionProps) => string | undefined;
  shouldDisplay?: (props: ProfileInfoSectionProps) => boolean;
  gridProps?: { padding?: number };
  icon: ReactNode;
  fieldName?: keyof UserData;
  editable?: boolean;
  customOnClick?: () => void;
  size?: number;
  disabled?: boolean;
  disabledMessage?: string;
};

const messages = defineMessages({
  name: { id: 'myProfile.name', defaultMessage: 'Jméno a příjmení' },
  email: { id: 'myProfile.email', defaultMessage: 'E-mail' },
  contactEmail: { id: 'myProfile.contactEmail', defaultMessage: 'Kontaktní e-mail' },
  confirmId: { id: 'myProfile.confirmId', defaultMessage: 'Souhlasné ID' },
  bankAccount: { id: 'myProfile.bankAccount', defaultMessage: 'Číslo bankovního účtu' },
  reviewUrl: { id: 'myProfile.reviewUrl', defaultMessage: 'Odkaz na recenze' },
  loyaltyPercent: { id: 'myProfile.loyaltyPercent', defaultMessage: 'Věrnostní program' },
  reservationLink: {
    id: 'myProfile.reservationLink',
    defaultMessage: 'Odkaz pro zákaznické rezervace (na váš web, Google nebo sociální sítě)',
  },
  bankAlias: {
    id: 'myProfile.bankAlias',
    defaultMessage: 'E-mail pro párování plateb',
  },
  bankAliasCopied: {
    id: 'myProfile.bankAliasCopied',
    defaultMessage: 'Zkopírováno! Nastavte si přeposílání bankovních notifikací na tento e-mail.',
  },
  expertOnly: {
    id: 'visitDetailGrid.soldItemsRevenueDisabledMessage',
    defaultMessage: 'Dostupné v plánu EXPERT. Aktivujte ve vašem profilu.',
  },
  address: { id: 'myProfile.address', defaultMessage: 'Adresa provozovny' },
  bio: { id: 'myProfile.bio', defaultMessage: 'O podniku' },
  publicProfileLink: {
    id: 'myProfile.publicProfileLink',
    defaultMessage: 'Veřejný profil (odkaz pro zákazníky)',
  },
});

const getUserProfileFieldsConfig = (
  isExpertPlan: boolean,
  handleCopyBankAlias: () => void,
  bankAlias: string | undefined,
  aliasCopied: boolean,
  intl: IntlShape,
): ProfileFieldConfig[] => [
  {
    descriptor: messages.name,
    getValue: ({ userData }) => userData.name,
    gridProps: { padding: 0 },
    size: 12,
    icon: <PersonOutlinedIcon sx={{ color: AppTheme.palette.common.white }} />,
    fieldName: 'name',
    editable: true,
  },
  {
    descriptor: messages.email,
    size: 12,
    getValue: ({ userEmail }) => userEmail,
    icon: <EmailOutlinedIcon sx={{ color: AppTheme.palette.common.white }} />,
    editable: false,
  },
  {
    descriptor: messages.contactEmail,
    getValue: ({ userData, userEmail }) => userData.contactEmail || userEmail,
    icon: <EmailOutlinedIcon sx={{ color: AppTheme.palette.common.white }} />,
    editable: true,
    fieldName: 'contactEmail',
    size: 12,
  },
  {
    descriptor: messages.confirmId,
    getValue: ({ userSub }) => userSub?.slice(-4),
    icon: <VerifiedUserOutlinedIcon sx={{ color: AppTheme.palette.common.white }} />,
    editable: false,
  },
  {
    descriptor: messages.bankAccount,
    getValue: ({ userData }) => userData.bankAccount,
    icon: <AccountBalanceOutlinedIcon sx={{ color: AppTheme.palette.common.white }} />,
    editable: true,
    fieldName: 'bankAccount',
  },
  {
    descriptor: messages.reviewUrl,
    getValue: ({ userData }) => userData.reviewUrl,
    icon: <RateReviewOutlinedIcon sx={{ color: AppTheme.palette.common.white }} />,
    editable: true,
    fieldName: 'reviewUrl',
  },
  {
    descriptor: messages.loyaltyPercent,
    getValue: ({ userData }) => `${userData.loyaltyPercent} %`,
    shouldDisplay: ({ isExpertPlan }) => isExpertPlan,
    icon: <LoyaltyOutlinedIcon sx={{ color: AppTheme.palette.common.white }} />,
    editable: true,
    fieldName: 'loyaltyPercent',
    disabled: !isExpertPlan,
    disabledMessage: messages.expertOnly.defaultMessage,
  },
  {
    descriptor: messages.bankAlias,
    getValue: () => (aliasCopied ? intl.formatMessage(messages.bankAliasCopied) : bankAlias),
    shouldDisplay: ({ isExpertPlan }) => isExpertPlan && Boolean(bankAlias),
    icon: <AlternateEmailOutlinedIcon sx={{ color: AppTheme.palette.common.white }} />,
    customOnClick: handleCopyBankAlias,
    size: 12,
    disabled: !isExpertPlan,
    disabledMessage: messages.expertOnly.defaultMessage,
  },
];

const getPublicProfileUrl = (userData: UserData) => {
  if (!userData.id || !userData.phone || !userData.address) {
    return null;
  }

  return `${window.location.origin}/public-reservation/profile/${userData.publicId}`;
};

const ProfileInfoSection = (props: ProfileInfoSectionProps) => {
  const intl = useIntl();
  const dialogRef = useRef<ImperativeFormDialogRefType>(null);
  const plan = useSubscriptionPlan();
  const isExpertPlan = isSubscriptionExpert(plan);
  const [copied, setCopied] = useState(false);
  const [aliasCopied, setAliasCopied] = useState(false);
  const { data: subscription } = useSubscriptionQuery();

  const remainingDays = subscription?.endDate ? calculateRemainingDays(subscription.endDate) : null;

  const publicProfileUrl = getPublicProfileUrl(props.userData);

  const [publicCopied, setPublicCopied] = useState(false);

  const bankAlias = generateBankAlias(props.userSub);

  const handleCopyPublicProfileLink = () => {
    if (!publicProfileUrl) return;
    navigator.clipboard.writeText(publicProfileUrl).then(() => {
      setPublicCopied(true);
      setTimeout(() => setPublicCopied(false), 2000);
    });
  };

  const handleCopyBankAlias = () => {
    if (!bankAlias) {
      return;
    }
    navigator.clipboard.writeText(bankAlias).then(() => {
      setAliasCopied(true);
      setTimeout(() => setAliasCopied(false), 2000);
    });
  };

  const { mutation: updateUser } = useUpdateUserMutation();

  const handleTogglePublicReservations = (enabled: boolean) => {
    updateUser.mutate({ allowPublicReservations: enabled });
  };

  const userProfileColumnConfig: ProfileFieldConfig[] = [
    ...getUserProfileFieldsConfig(isExpertPlan, handleCopyBankAlias, bankAlias, aliasCopied, intl),
  ];

  const publicProfileFields: ProfileFieldConfig[] = [
    {
      descriptor: messages.publicProfileLink,
      getValue: () =>
        publicCopied
          ? intl.formatMessage({
              id: 'common.copied',
              defaultMessage: 'Zkopírováno! Vložte odkaz na sociální sítě.',
            })
          : publicProfileUrl || 'Nedostupné (vyplňte bio, telefon a adresu provozovny)',
      icon: <ContentCopyOutlinedIcon sx={{ color: AppTheme.palette.common.white }} />,
      customOnClick: handleCopyPublicProfileLink,
      size: 12,
    },
    {
      descriptor: messages.address,
      getValue: ({ userData }) => userData.address || '—',
      icon: <></>,
      fieldName: 'address',
      editable: true,
      size: 12,
    },
    {
      descriptor: messages.bio,
      getValue: ({ userData }) => userData.bio || '—',
      icon: <></>,
      fieldName: 'bio',
      editable: true,
      size: 12,
    },
  ];
  void publicProfileFields; // odstraní lint warning — data jsou ve PublicProfileSection

  return (
    <>
      <UserProfilDialog ref={dialogRef} />
      {subscription && remainingDays !== null && (
        <Box sx={{ mb: 1 }}>
          <Stack direction="row" spacing={1} alignItems="center" justifyContent="center">
            <Chip
              icon={<WorkspacePremiumOutlinedIcon />}
              label={intl.formatMessage(
                { id: 'myProfile.subscriptionPlan', defaultMessage: 'Tarif {plan}' },
                {
                  plan: `${subscription.plan.toUpperCase()}${subscription.billingPeriod === 'YEARLY' ? ' · roční' : ''}`,
                },
              )}
              color="primary"
              variant="outlined"
              size="small"
            />
            <Chip
              icon={<CalendarMonthOutlinedIcon />}
              label={intl.formatMessage(
                {
                  id: 'myProfile.subscriptionDaysLeft',
                  defaultMessage: '{days} dní do konce',
                },
                { days: remainingDays },
              )}
              color={remainingDays <= 7 ? 'error' : remainingDays <= 14 ? 'warning' : 'success'}
              variant="outlined"
              size="small"
            />
          </Stack>
        </Box>
      )}
      <Grid container rowSpacing={2} columnSpacing={2}>
        {userProfileColumnConfig.map(field => {
          if (field.shouldDisplay && !field.shouldDisplay(props)) {
            return null;
          }

          return (
            <Grid key={field.descriptor.id} size={field.size ?? 6} {...field.gridProps}>
              <DetailColumn
                {...(field.customOnClick
                  ? { onClick: field.customOnClick }
                  : field.editable
                    ? { onClick: () => dialogRef.current?.open(field.fieldName) }
                    : {})}
                label={intl.formatMessage(field.descriptor)}
                iconBackgroundColor="#1db5c9ff"
                icon={field.icon}
                input={field.getValue(props)}
                disabled={field.disabled}
                disabledMessage={field.disabledMessage}
              />
            </Grid>
          );
        })}
      </Grid>

      <PublicProfileSection
        disabled={!isExpertPlan}
        userData={props.userData}
        userEmail={props.userEmail}
        dialogRef={dialogRef}
        publicProfileUrl={publicProfileUrl}
        publicCopied={publicCopied}
        onCopyPublicLink={handleCopyPublicProfileLink}
        onToggleReservations={handleTogglePublicReservations}
      />
    </>
  );
};

export default ProfileInfoSection;
